import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import { ChatInput } from './ChatInput';
import { FileAttachmentsList } from './FileAttachmentsList';
import { FileViewerModal, Button } from '../ui';
import { MessageList } from '../messages';
import { ArrowDown } from 'lucide-react';
import { cn } from '../../utils';
import { useChat } from '../../hooks/useChat';
import { ENABLE_FILE_UPLOAD } from '../../utils/constants';
import type { ModelName, FileAttachment } from '../../types';
import './Chat.css';

interface ChatProps {
  className?: string;
  initialModel?: string;
  onError?: (error: Error) => void;
}

const ChatComponent: React.FC<ChatProps> = ({ 
  className, 
  initialModel,
  onError 
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [viewingFile, setViewingFile] = useState<FileAttachment | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isLoadingHistory,
    sendMessage,
    error,
    clearError,
  } = useChat({
    initialModel: initialModel as ModelName | undefined,
    onError,
  });

  const chatStarted = messages.length > 0;

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
      
      setTimeout(() => {
        setShowScrollButton(false);
      }, 300);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    
    userScrollingRef.current = true;
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      userScrollingRef.current = false;
    }, 150);
    
    setShowScrollButton(!isAtBottom);
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if ((!inputValue.trim() && (!ENABLE_FILE_UPLOAD || attachments.length === 0)) || isLoading) return;
    
    const messageText = inputValue.trim();
    setInputValue('');
    sendMessage(messageText, ENABLE_FILE_UPLOAD ? attachments : undefined);
    setAttachments([]);
  }, [inputValue, attachments, isLoading, setInputValue, sendMessage]);

  const handleRemoveAttachment = useCallback((attachmentId: string) => {
    setAttachments(prev => prev.filter(attachment => attachment.id !== attachmentId));
  }, [setAttachments]);

  const handleViewAttachment = useCallback((attachment: FileAttachment) => {
    setViewingFile(attachment);
  }, []);

  const handleCloseModal = useCallback(() => {
    setViewingFile(null);
  }, []);

  const prevMessagesRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length === 0) return;
    
    if (messages.length > prevMessagesRef.current) {
      prevMessagesRef.current = messages.length;
      
      if (!userScrollingRef.current) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (isLoadingHistory) {
    return (
      <div className={cn('chat', className, 'chat--loading')}>
        <div className="chat__loading">
          <div className="chat__loading-spinner"></div>
          <p className="chat__loading-text">Загрузка истории чата...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('chat', className, chatStarted && 'chat--started')}>
      {chatStarted ? (
        <>
          <div 
            ref={scrollRef}
            className="chat__messages"
            onScroll={handleScroll}
          >
            <MessageList 
              messages={messages} 
              isLoading={isLoading}
              onViewAttachment={handleViewAttachment}
            />
          </div>

          {showScrollButton && (
            <div className="chat__scroll-button">
              <Button 
                variant="outline" 
                onClick={scrollToBottom}
                className="scroll-button"
              >
                <ArrowDown size={16} />
                <span>К новым ответам</span>
              </Button>
            </div>
          )}

          <div className="chat__input-container">
            {/* File attachments above input in chat */}
            {ENABLE_FILE_UPLOAD && attachments.length > 0 && (
              <FileAttachmentsList
                attachments={attachments}
                onRemoveAttachment={handleRemoveAttachment}
                onViewAttachment={handleViewAttachment}
                showRemoveButton={true}
                className="chat__attachments-above"
                compactMode={true}
                reverseOrder={true}
              />
            )}
            
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              attachments={ENABLE_FILE_UPLOAD ? attachments : undefined}
              onAttachmentsChange={ENABLE_FILE_UPLOAD ? setAttachments : undefined}
            />
          </div>
        </>
      ) : (
        <div className="chat__initial">
          <div className="chat__initial-content">
            <div className="chat__welcome">
              <h2 className="chat__welcome-title">Добро пожаловать!</h2>
              <p className="chat__welcome-subtitle">
                Начните разговор с AI-ассистентом
              </p>
            </div>
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              className="chat__initial-input"
              placeholder="Введите ваше сообщение..."
              attachments={ENABLE_FILE_UPLOAD ? attachments : undefined}
              onAttachmentsChange={ENABLE_FILE_UPLOAD ? setAttachments : undefined}
            />
            
            {/* File attachments below input on main screen */}
            {ENABLE_FILE_UPLOAD && attachments.length > 0 && (
              <FileAttachmentsList
                attachments={attachments}
                onRemoveAttachment={handleRemoveAttachment}
                onViewAttachment={handleViewAttachment}
                showRemoveButton={true}
                className="chat__attachments-below"
              />
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="chat__error">
          <div className="chat__error-content">
            <span className="chat__error-text">{error}</span>
            <button 
              className="chat__error-close"
              onClick={clearError}
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* File viewer modal */}
      <FileViewerModal
        file={viewingFile}
        isOpen={viewingFile !== null}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export const Chat = memo(ChatComponent);
